import { PhotoCameraOutlined } from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  TextFieldProps
} from "@mui/material";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ScanInputOption {
  value: string;
  label: string;
}

interface ScanInputProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  options?: ScanInputOption[];
  helperText?: ReactNode;
  error?: boolean;
  disabled?: boolean;
  required?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  testId?: string;
  submitTestId?: string;
  submitLabel?: string;
  submitting?: boolean;
  onSubmit?: (value: string) => unknown | Promise<unknown>;
  submitOnOptionSelect?: boolean;
  cameraEnabled?: boolean;
  textFieldProps?: Omit<TextFieldProps,
    "value" | "onChange" | "label" | "helperText" | "error">;
}

export function normalizeScanValue(value: string): string {
  return value.trim();
}

export function ScanInput({
  value,
  onChange,
  label,
  options = [],
  helperText,
  error = false,
  disabled = false,
  required = false,
  autoFocus = false,
  placeholder,
  testId,
  submitTestId,
  submitLabel,
  submitting = false,
  onSubmit,
  submitOnOptionSelect = true,
  cameraEnabled = true,
  textFieldProps
}: ScanInputProps) {
  const { t } = useTranslation();
  const resolvedSubmitLabel = submitLabel ?? t("transverse.scan.validate");
  const submitLock = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const submit = async (submittedValue = value) => {
    const normalized = normalizeScanValue(submittedValue);
    if (!onSubmit || !normalized || disabled || submitting || submitLock.current) return;
    submitLock.current = true;
    try {
      onChange(normalized);
      await onSubmit(normalized);
    } finally {
      submitLock.current = false;
    }
  };

  const closeCamera = () => {
    if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
    animationRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
  };

  useEffect(() => () => {
    if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
  }, []);

  const openCamera = async () => {
    setCameraError(null);
    setCameraOpen(true);
    try {
      const Detector = (window as unknown as {
        BarcodeDetector?: new (options?: { formats?: string[] }) => {
          detect: (source: CanvasImageSource) => Promise<Array<{ rawValue: string }>>;
        };
      }).BarcodeDetector;
      if (!Detector) {
        throw new Error(t("transverse.scan.unsupported"));
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error(t("transverse.scan.denied"));
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }, audio: false
      });
      streamRef.current = stream;
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      const video = videoRef.current;
      if (!video) throw new Error(t("transverse.scan.preview"));
      video.srcObject = stream;
      await video.play();
      const detector = new Detector({
        formats: ["code_128", "code_39", "ean_13", "ean_8", "qr_code", "data_matrix"]
      });
      const detect = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          const scannedValue = normalizeScanValue(codes[0]?.rawValue ?? "");
          if (scannedValue) {
            onChange(scannedValue);
            closeCamera();
            if (onSubmit) await submit(scannedValue);
            return;
          }
        } catch {
          // Une image momentanément illisible ne doit pas interrompre le scan.
        }
        animationRef.current = requestAnimationFrame(() => void detect());
      };
      animationRef.current = requestAnimationFrame(() => void detect());
    } catch (error) {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
      setCameraError(error instanceof Error ? error.message : t("transverse.scan.openError"));
    }
  };

  const field = (
    <Autocomplete<ScanInputOption, false, false, true>
      freeSolo
      forcePopupIcon={options.length > 0}
      openOnFocus={options.length > 0}
      autoHighlight
      disabled={disabled}
      options={options}
      filterOptions={(items, state) => {
        const query = state.inputValue.trim().toLocaleLowerCase("fr");
        if (!query) return items;
        return items.filter((item) =>
          item.value.toLocaleLowerCase("fr").includes(query)
          || item.label.toLocaleLowerCase("fr").includes(query));
      }}
      getOptionLabel={(option) => typeof option === "string" ? option : option.label}
      inputValue={value}
      onInputChange={(_, nextValue, reason) => {
        if (reason !== "reset") onChange(nextValue);
      }}
      onChange={(_, option) => {
        const selectedValue = typeof option === "string"
          ? option : option?.value ?? "";
        onChange(selectedValue);
        if (selectedValue && onSubmit && submitOnOptionSelect) {
          void submit(selectedValue);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          autoFocus={autoFocus}
          required={required}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          data-testid={testId}
          inputProps={{
            ...params.inputProps,
            ...textFieldProps?.inputProps,
            autoComplete: "off",
            autoCapitalize: "characters"
          }}
          onKeyDown={(event) => {
            textFieldProps?.onKeyDown?.(event);
            if (!event.defaultPrevented && event.key === "Enter" && onSubmit) {
              event.preventDefault();
              void submit(value);
            }
          }}
        />
      )}
    />
  );

  return (
    <>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
        alignItems={{ sm: "flex-start" }}>
        <Stack sx={{ flex: 1 }}>{field}</Stack>
        {cameraEnabled && (
          <Button variant="outlined" startIcon={<PhotoCameraOutlined />}
            disabled={disabled || submitting}
            onClick={() => void openCamera()}
            sx={{ minHeight: 56, whiteSpace: "nowrap" }}>
            {t("transverse.scan.camera")}
          </Button>
        )}
        {onSubmit && (
          <Button variant="contained" data-testid={submitTestId}
            disabled={disabled || submitting || !normalizeScanValue(value)}
            onClick={() => void submit(value)}
            sx={{ minHeight: 56, whiteSpace: "nowrap" }}>
            {resolvedSubmitLabel}
          </Button>
        )}
      </Stack>
      <Dialog open={cameraOpen} onClose={closeCamera} fullWidth maxWidth="sm">
        <DialogTitle>{t("transverse.scan.title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {cameraError && <Alert severity="error">{cameraError}</Alert>}
            <video ref={videoRef} muted playsInline style={{
              display: cameraError ? "none" : "block", width: "100%",
              minHeight: 240, background: "#111", borderRadius: 8
            }} />
            {!cameraError && (
              <Alert severity="info">{t("transverse.scan.placement")}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCamera}>{t("common.actions.close")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
